import { useId } from 'react';

import { cn } from '../lib/cn';

/**
 * The Wend mark.
 *
 * A dot at the lower left, a white path that steps up and to the right, and a
 * gradient ring at its end. It reads as a route taken deliberately — which is
 * what "wend" means, and what the product does to a customer journey.
 *
 * Geometry is fixed and must not be redrawn by eye: the ring gradient runs
 * bottom-left to top-right in user space, so it stays consistent at every size.
 * Colours are literal rather than themed — this is a brand asset, and it has to
 * survive being rendered on a background the theme does not control.
 */
export interface WendMarkProps {
  className?: string;
  /** Draw the dark tile behind the mark. Off gives a transparent ground. */
  ground?: boolean;
  /** Accessible name. Omit for decorative use, where it renders aria-hidden. */
  title?: string;
}

export function WendMark({ className, ground = true, title }: WendMarkProps) {
  // useId can emit characters that are illegal inside url(#…).
  const gradientId = `wend-ring-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <svg
      viewBox="0 0 220 220"
      className={cn('size-7 shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <linearGradient id={gradientId} x1="99" y1="51" x2="141" y2="9" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6D5EF6" />
          <stop offset="1" stopColor="#9B5EF6" />
        </linearGradient>
      </defs>
      {ground ? <rect width="220" height="220" rx="48" fill="#2A2150" /> : null}
      <g transform="translate(7.4,8.6) scale(1.2)">
        <circle cx="40" cy="150" r="10" fill="#FFFFFF" />
        <path
          d="M40 150 L40 96 Q40 84 52 84 L108 84 Q120 84 120 72 L120 45"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <circle cx="120" cy="30" r="15" fill="none" stroke={`url(#${gradientId})`} strokeWidth="12" />
      </g>
    </svg>
  );
}
